/**
 *  PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 *  Copyright © 2024 Pearson Education, Inc.
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 * */
/**
 * @file StudioTextConstants.jsx - A component that handles text or canned message constants.
 */

/** MUI Menu / Popover — anchor & transform origins (e.g. SidePanel New Graph submenu opens right of the button) */
export const MENU_ANCHOR_ORIGIN_TOP_RIGHT = { vertical: 'top', horizontal: 'right' };
export const MENU_TRANSFORM_ORIGIN_TOP_LEFT = { vertical: 'top', horizontal: 'left' };

// Agent Icon name
export const AGENT_ICON = 'AgentIcon';

// Common Constants
export const TENANT_ID = 'tenantId';
export const BERLIN_TENANT_ID_HEADER = 'x-berlin-tenant-id';
export const CUSTOM_PROPERTY_BOOK_ID_KEYS = ['bookID', 'bookId', 'bookid'];
export const CUSTOM_PROPERTY_PRODUCT_MODEL_KEYS = ['productModel', 'productmodel'];

// Conditional Edge Texts
export const CONDITIONAL_EDGE = 'Conditional Edge';
export const CONDITIONAL_EDGE_DESCRIPTION = 'Check this before creating an edge to make it conditional (dashed)';
export const CONDITIONAL_EDGE_TIP = 'Tip: Select nodes & press Delete or right-click.';

// Graph Icon Label
export const NEW_GRAPH = 'New Graph';
export const LOAD_GRAPH = 'Load Graph';
export const SAVE_GRAPH = 'Save Graph';
export const RESET_GRAPH = 'Reset Graph';
export const COPY_GRAPH = 'Copy Graph';
export const CONFIGURE_NODE = 'Configure Node';
export const PUBLISH_GRAPH = 'Publish Graph';
export const PROMOTE_GRAPH = 'Promote Graph';
export const DASHBOARD = 'Dashboard';
export const LOAD_TEMPLATE = 'Load Template';
export const NEW_TEMPLATE = 'New Template';

// Content Section Labels
export const UPLOAD_CONTENT = 'Upload content';
export const CONTENT_DRAWER = 'Content Drawer';
export const UPLOAD_CONTENT_KEY = 'uploadContent';
export const CONTENT_DRAWER_KEY = 'contentDrawer';

// Graph Node Labels
export const ADD_START_NODE = 'Add START Node';
export const ADD_SUPERVISOR_NODE = 'Add Supervisor Node';
export const ADD_AGENT_NODE = 'Add Agent Node';
export const ADD_TOOL_NODE = 'Add Tool Node';
export const ADD_SKILL_NODE = 'Add Skill Node';
export const ADD_GRAPH_NODE = 'Add Graph Node';
export const ADD_END_NODE = 'Add END Node';
export const GROUP_UNGROUP = 'Toggle group';
export const LABEL_PARSERS = 'Parsers';

// Agent Type
export const AGENT_TYPE = {
  NONE: '',
  CLASSIFIER: 'classifier',
  ROUTER_SUBAGENT: 'routerSubagent',
  SYNTHESIZER: 'synthesizer',
  SUBAGENT: 'subagent',
  HANDOFF: 'handoff'
};

export const AGENT_TYPE_LABEL = {
  [AGENT_TYPE.NONE]: 'None',
  [AGENT_TYPE.CLASSIFIER]: AGENT_TYPE.CLASSIFIER,
  [AGENT_TYPE.ROUTER_SUBAGENT]: AGENT_TYPE.ROUTER_SUBAGENT,
  [AGENT_TYPE.SYNTHESIZER]: AGENT_TYPE.SYNTHESIZER,
  [AGENT_TYPE.SUBAGENT]: AGENT_TYPE.SUBAGENT,
  [AGENT_TYPE.HANDOFF]: AGENT_TYPE.HANDOFF
};

// Parameter types for tool/skill parameters
export const PARAMETER_TYPE = {
  NUMBER: 'number',
  STRING: 'string',
  BOOLEAN: 'boolean',
  STRING_ARRAY: 'stringArray',
  BOOLEAN_ARRAY: 'booleanArray',
  NUMBER_ARRAY: 'numberArray',
  OBJECT: 'object',
  OBJECT_ARRAY: 'objectArray',
  ANY: 'any'
};

export const BERLIN_API_AUTH = {
  Unauthorized: 'Unauthorized: Please login again',
  Forbidden: 'Forbidden: You do not have permission to perform this action',
  badRequest: 'Bad request',
  FailedTofetchAgentCard: 'Failed to fetch agent card',
  InvalidAgentConfig: 'Bad request: Invalid agent configuration'
};

export const PARAMETER_TYPE_OPTIONS = [
  { value: PARAMETER_TYPE.NUMBER, label: 'number' },
  { value: PARAMETER_TYPE.STRING, label: 'string' },
  { value: PARAMETER_TYPE.BOOLEAN, label: 'boolean' },
  { value: PARAMETER_TYPE.STRING_ARRAY, label: 'array of string' },
  { value: PARAMETER_TYPE.BOOLEAN_ARRAY, label: 'array of boolean' },
  { value: PARAMETER_TYPE.NUMBER_ARRAY, label: 'array of number' },
  { value: PARAMETER_TYPE.OBJECT, label: 'object' },
  { value: PARAMETER_TYPE.OBJECT_ARRAY, label: 'object array' },
  { value: PARAMETER_TYPE.ANY, label: 'any' }
];

// Graph workflow pattern
export const GRAPH_PATTERN = {
  SUBAGENT: 'subagent',
  HANDOFF: 'handoff',
  ROUTER: 'router',
  CUSTOM: 'custom',
  SKILLS: 'skills'
};

export const ENABLE_MEMORY_SAVER_KEY = 'enableMemorySaver';
export const ENABLE_AGENT_THINKING_MSG_KEY = 'enableAgentThinkingMsg';

/** Result reasons from Advanced Settings audio provider fetch */
export const ADVANCED_SETTINGS_AUDIO_FETCH_REASON = {
  MISSING_TENANT: 'missing_tenant',
  FETCH_ERROR: 'fetch_error'
};

export const PATTERN_OPTIONS = [
  { value: GRAPH_PATTERN.SUBAGENT, label: GRAPH_PATTERN.SUBAGENT },
  { value: GRAPH_PATTERN.HANDOFF, label: GRAPH_PATTERN.HANDOFF },
  { value: GRAPH_PATTERN.ROUTER, label: GRAPH_PATTERN.ROUTER },
  { value: GRAPH_PATTERN.CUSTOM, label: GRAPH_PATTERN.CUSTOM },
  { value: GRAPH_PATTERN.SKILLS, label: GRAPH_PATTERN.SKILLS }
];

export const PATTERN_AGENT_TYPE_MAP = {
  [GRAPH_PATTERN.SUBAGENT]: [AGENT_TYPE.SUBAGENT],
  [GRAPH_PATTERN.HANDOFF]: [AGENT_TYPE.HANDOFF],
  [GRAPH_PATTERN.ROUTER]: [AGENT_TYPE.CLASSIFIER, AGENT_TYPE.ROUTER_SUBAGENT, AGENT_TYPE.SYNTHESIZER],
  [GRAPH_PATTERN.SKILLS]: [AGENT_TYPE.SUBAGENT, AGENT_TYPE.HANDOFF, AGENT_TYPE.ROUTER_SUBAGENT, AGENT_TYPE.SYNTHESIZER],
  [GRAPH_PATTERN.CUSTOM]: [
    AGENT_TYPE.SUBAGENT,
    AGENT_TYPE.HANDOFF,
    AGENT_TYPE.CLASSIFIER,
    AGENT_TYPE.ROUTER_SUBAGENT,
    AGENT_TYPE.SYNTHESIZER
  ]
};

export const getAllowedAgentTypesForPattern = (pattern) => {
  if (!pattern) return PATTERN_AGENT_TYPE_MAP[GRAPH_PATTERN.CUSTOM];
  return PATTERN_AGENT_TYPE_MAP[pattern] || PATTERN_AGENT_TYPE_MAP[GRAPH_PATTERN.CUSTOM];
};

export const isSkillNodeAllowedForPattern = (pattern) =>
  pattern === GRAPH_PATTERN.SKILLS || pattern === GRAPH_PATTERN.CUSTOM;

export const ENABLE_SUPERVISOR_NODE_CREATION = false;

// Graph Node type
export const NODE_TYPE = {
  START: 'start',
  SUPERVISOR: 'supervisor',
  AGENT: 'agent',
  TOOL: 'tool',
  SKILL: 'skill',
  GRAPH: 'graph',
  END: 'end',
  GROUP: 'group',
  DEFAULT: 'default'
};

// For backward compatibility - individual exports using destructuring
export const { START, SUPERVISOR, AGENT, TOOL, SKILL, GRAPH, END, GROUP } = NODE_TYPE;

/** Node types that open the edit/configure modal on double-click (see GraphEditor / useInjectNodeDoubleClick). */
export const NODE_TYPES_WITH_CONFIGURE_MODAL = [
  NODE_TYPE.AGENT,
  NODE_TYPE.TOOL,
  NODE_TYPE.SKILL,
  NODE_TYPE.GRAPH,
  NODE_TYPE.SUPERVISOR
];

// Edge type constants
export const EDGE_TYPE = {
  CUSTOM: 'custom'
};

// Default edge label
export const DEFAULT_EDGE_LABEL = 'continue';
export const DEFAULT_CONDITION_EXPRESSION = 'state.messages.at(-1).content.includes("summarize")';

// Graph Node action keys
export const CREATE = 'create';
export const RESET = 'reset';
export const COPY = 'copy';
export const LOAD = 'load';
export const SAVE = 'save';
export const PUBLISH = 'publish';
export const PROMOTE = 'promote';

// Graph Save Type Constants (internal state values, not UI labels)
export const SAVE_TYPE = {
  COPY: 'copy',
  SAVE_CHANGES: 'saveChanges',
  PUBLISH_CHANGES: 'publishChanges',
  COPY_GRAPH: 'copyGraph',
  PROMOTE_GRAPH: 'promoteGraph',
  GRAPH_NAME_CHANGES: 'graphNameChanges'
};

// Graph Operation Types (for API routing)
export const GRAPH_OPERATION = {
  CREATE: 'CREATE',
  TEMPLATE_CREATE: 'TEMPLATE_CREATE',
  UPDATE: 'UPDATE',
  CREATE_VERSION: 'CREATE_VERSION',
  PUBLISH: 'PUBLISH',
  COPY: 'COPY',
  PROMOTE: 'PROMOTE'
};
// Modal config types
export const COPY_GRAPH_CONFIG = 'copyGraph';
export const PROMOTE_GRAPH_CONFIG = 'promoteGraph';
export const PUBLISH_CHANGES_CONFIG = 'publishChanges';

// Header constants texts
export const SELECT_TENANT = 'Select Tenant';
export const BERLIN_STUDIO = 'Berlin Studio';
export const BERLIN_STUDIO_HELP_URL =
  'https://pearson.atlassian.net/wiki/spaces/ETXT/pages/77081242/Getting+Started+with+Berlin+Platform';

// Graph lifecycle statuses
export const GRAPH_STATUS_DRAFT = 'DRAFT';

// Sensitive header keys for security masking
export const SENSITIVE_HEADER_KEYS = ['key', 'password', 'x-authorization', 'auth', 'api-key', 'api'];

// Node button constants
export const DETACH = 'Detach';
export const DETACH_FROM_GROUP = 'Detach from group';
export const UN_GROUP = 'Ungroup';
export const UN_GROUP_NODES = 'Ungroup nodes';

// Agentic Roles
export const GRAPH_PERMISSION_PREFIX = 'graph_';
export const GRAPH_VIEW_ACCESS = 'graph_can_view';
export const GRAPH_EDIT_ACCESS = 'graph_can_edit';
export const EVAL_CAN_VIEW = 'eval_can_view';
export const EVAL_CAN_EDIT = 'eval_can_edit';

export const RUN_EVAL_API_PATH = '/evaluation';

/** API / UI values for evaluation schedule */
export const EVAL_SCHEDULE_DAILY = 'daily';
export const EVAL_SCHEDULE_WEEKLY = 'weekly';
export const EVAL_SCHEDULE_MONTHLY = 'monthly';

/** Dayjs / scheduler datetime format strings */
export const DateFormat = Object.freeze({
  EVAL_SCHEDULE_DISPLAY: 'MM/DD/YYYY HH:mm',
  SCHEDULER_DATETIME: 'YYYY-MM-DDTHH:mm:ss'
});

/** Evaluation criteria form defaults and limits */
export const EVAL_CRITERIA_DEFAULT_SAMPLE_PERCENT = '0';
export const EVAL_SAMPLE_PERCENT_MIN = 0;
export const EVAL_SAMPLE_PERCENT_MAX = 100;
export const EVAL_SAMPLE_PERCENT_MAX_LENGTH = 3;

/** Save button phases (local UI state) */
export const EVAL_SAVE_PHASE_IDLE = 'idle';
export const EVAL_SAVE_PHASE_SAVING = 'saving';
export const EVAL_SAVE_PHASE_SAVED = 'saved';

/** LogsViewer content variants */
export const LOGS_VIEWER_CONTENT_VARIANT_JSON = 'json';
export const LOGS_VIEWER_CONTENT_VARIANT_EVAL_LENS = 'evalLens';
export const LOGS_VIEWER_CONTENT_VARIANTS = [LOGS_VIEWER_CONTENT_VARIANT_JSON, LOGS_VIEWER_CONTENT_VARIANT_EVAL_LENS];

/** Eval Lens structured feedback view model result types */
export const EVAL_LENS_FEEDBACK_VIEW_OK = 'ok';
export const EVAL_LENS_FEEDBACK_VIEW_EMPTY = 'empty';
export const EVAL_LENS_FEEDBACK_VIEW_PAYLOAD_ERROR = 'payloadError';

/** DOM ids for evaluation criteria fields */
export const EVAL_CRITERIA_ELEMENT_IDS = {
  RUBRIC_NAME: 'evaluation-criteria-rubric-name',
  SAMPLE_PERCENT: 'evaluation-criteria-sample-percent',
  DATE_TIME_LOCAL: 'evaluation-criteria-date-time-local',
  MARKDOWN: 'evaluation-criteria-markdown',
  EVAL_TYPES: 'evaluation-criteria-eval-types'
};

export const EVAL_PANEL_VIEW_LIST = 'list';
export const EVAL_PANEL_VIEW_DETAIL = 'detail';

export const EVAL_CONFIRM_MODE_DISCARD = 'discard';
export const EVAL_CONFIRM_MODE_DELETE = 'delete';

/** Display-only label when legacy evaluation rows have no rubricName (list / graph tester). */
export const LEGACY_RUBRIC_LIST_DISPLAY_NAME = 'Rubric 1';

/** Above this count, the types picker splits options evenly across two table columns */
export const EVAL_TYPES_SINGLE_TABLE_MAX = 10;

export const GRAPH_PUBLISH_PUBISH = 'graph_can_publish';
export const GRAPH_PROMOTE_ACCESS = 'graph_can_promote';
export const PREBUILT_GRAPH_EDIT_ACCESS = 'prebuilt_graph_can_edit';

// Template library / create-from-template (New Graph submenu)
export const TEMPLATE_CAN_VIEW = 'template_can_view';
export const TEMPLATE_CAN_EDIT = 'template_can_edit';

// Content upload (Upload content + Content Drawer in Studio side panel)
export const CONTENT_UPLOAD_EDIT_ACCESS = 'contentupload_can_edit';

// Improve Prompt / Prompt Generator (agent system prompt editor)
export const PROMPT_IMPROVE_CAN_VIEW = 'promptimprove_can_view';

// Advance Mode (Berlin Studio panel in graph editor)
export const ADVANCE_MODE_CAN_VIEW = 'advancemode_can_view';

// VibeFlow / Build With Berlin
export const VIBEFLOW_CAN_VIEW = 'vibeflow_can_view';

export const IMPROVE_PROMPT_DIFF_SIDE = Object.freeze({
  LEFT: 'left',
  RIGHT: 'right'
});

// AI Model Names
export const MODEL_GPT_5 = 'gpt-5';
export const MODEL_GPT_5_MINI = 'gpt-5-mini';

// Model-specific constants
export const GPT_5_MIN_TEMPERATURE = 1;
export const GPT_5_MODELS = [MODEL_GPT_5, MODEL_GPT_5_MINI];

// Tenant Contants
export const DEFAULT_TENANT_NAME = 'aistudytool';
export const ADDITIONAL_METADATA_PLACEHOLDER = `Enter JSON like: ${JSON.stringify(
  {
    layout: {
      type: 'pills',
      position: 'top',
      alignment: 'left',
      direction: 'horizontal'
    }
  },
  null,
  2
)}`;

export const ADDITIONAL_METADATA_LABEL = 'Additional Metadata (JSON)';

export const CONNECTION_RULES = [
  'start → supervisor/agent (workflow starts with supervisor or agent)',
  'supervisor → agent/tool/graph (supervisor can connect to agents, tools and graphs)',
  'supervisor → end (supervisor can connect to end)',
  'agent → agent/tool/graph/end (agents can call other agents, tools, graphs, or end)',
  'tool → agent (tools return output to agents)',
  'No connections to start',
  'No connections from end',
  'No self connections'
];

export const INSTRUCTIONS_ARIA_LABEL = 'instructions';

export const GRAPH_CONNECTION_RULES = 'Graph Connection Rules';

// STREAMING CHUNKS & GO DEEPER constants
export const DONE = 'done';
export const VIEW = 'view';
export const CHUNK = 'chunk';
export const JSON_TEXT = 'JSON';
export const META_DATA = 'metadata';
export const STREAMING = 'streaming';
export const GO_DEEPER_MESSAGE = 'Ready to go deeper?';
export const GO_DEEPER_SUB_MESSAGE = 'Explore one of these follow-ups to push your understanding even further.';

// Dynamic Properties Editor
export const PROPERTY_MODES = {
  KEY_VALUE: 'keyValue',
  JSON: 'json'
};

export const PROPERTY_MODE_LABELS = {
  [PROPERTY_MODES.KEY_VALUE]: 'Key/Value',
  [PROPERTY_MODES.JSON]: 'JSON'
};

export const ADD_PROPERTY_BUTTON_LABEL = 'Add Parameters';

export const PROPERTY_JSON_PLACEHOLDER = `{
  "sampleKey": "value"
}`;

// Bot Name
export const BOT_NAME = 'Berlin Bot';

// Testbed Configuration Labels
export const GENERAL_LABEL = 'General';
export const PROBLEM_SOLVE_FEATURE_LABEL = 'Problem Solve Feature';
export const RECOMMENDED_PROMPTS_LABEL = 'Recommended Prompts';
export const HIGHER_ORDER_SUGGESTION_LABEL = 'Higher Order Suggestion';
export const NEW_VALIDATE_ANSWER_LABEL = 'New Validate Answer';
export const QUIZ_RANDOMIZATION_LABEL = 'Quiz Randomization';
export const AI_CHANNEL_VIDEOS_LABEL = 'AI Channel Videos';
export const AGENTIC_AI_LABEL = 'Agentic AI';
export const CHATBOT_AI_OPTIONS_LABEL = 'ChatBot AI Options';
export const SHOW_SUMMARY_LABEL = 'Show Summary';
export const SHOW_PRACTICE_LABEL = 'Show Practice';
export const SHOW_EXPLAIN_LABEL = 'Show Explain';

// Config Type Mapping Labels
export const TENANT_ID_LABEL = 'Tenant Id';
export const TENANT_KEY_LABEL = 'Tenant Key';
export const TENANT_NAME_LABEL = 'Tenant Name';
export const COURSE_ID_LABEL = 'Course Id';
export const PAGE_ID_LABEL = 'Page Id';
export const USER_ID_LABEL = 'User Id';
export const MESSAGE_LABEL = 'Message';
export const TUTOR_NAME_LABEL = 'Tutor Name';
export const BOT_NAME_LABEL = 'Bot Name';
export const ENVIRONMENT_LABEL = 'Environment';
export const EXPAND_MODE_LABEL = 'Expand Mode';
export const PRODUCT_MODEL_LABEL = 'Product Model';
export const CHANNEL_VIDEOS_LABEL = 'Channel Videos';
export const AGENTIC_LABEL = 'Agentic';
export const EXPLAIN_QUIZ_OPTIONS_LABEL = 'Explain Quiz Options';
export const GRAPH_NAME_LABEL = 'Graph Name';
export const GRAPH_ID_LABEL = 'Graph Id';
export const CHANNEL_ID_LABEL = 'Channel Id';
export const VERSION_LABEL = 'Version';
export const WELCOME_MESSAGE_FROM_TEST_BED_LABEL = 'Welcome Message From Test Bed';
export const WELCOME_MESSAGE_LABEL = 'Welcome Message';
export const WELCOME_BACK_MESSAGE_LABEL = 'Welcome Back Message';
export const BOOK_TITLE_LABEL = 'Book Title';
export const BOOK_TITLE_PRE_TEXT_LABEL = 'Book Title Pre Text';
export const BOOK_TITLE_SUB_TEXT_LABEL = 'Book Title Sub Text';
export const RECOMMENDATIONS_HEADING_LABEL = 'Recommendations Heading';
export const CTA_BUTTON_HEADING_WITH_PILLS_LABEL = 'CTA Button Heading With Pills';
export const CTA_BUTTON_HEADING_WITHOUT_PILLS_LABEL = 'CTA Button Heading Without Pills';
export const NEW_TOPIC_LABEL = 'New Topic';
export const NEW_TOPIC_SUB_MESSAGE_LABEL = 'New Topic Sub Message';
export const AUTO_OPEN_HEADING_MESSAGE_LABEL = 'Auto Open Heading Message';
export const AUTO_OPEN_WELCOME_MESSAGE_LABEL = 'Auto Open Welcome Message';
export const CTA_BUTTON_HEADING_QUIZ_STOP_MODE_LABEL = 'CTA Button Heading Quiz Stop Mode';
export const CUSTOM_PROPERTIES = 'Custom Properties';

// TestBed UI Text Constants
export const WELCOME_TO_TESTBED = 'Welcome to TestBed';
export const SHARE_GRAPH = 'Share Graph';
export const COPY_URL = 'Copy URL';

// ShareGraphDialog Text Constants
export const SHARE_GRAPH_TITLE = 'Share';
export const SHARE_GRAPH_SUBTITLE = 'Share with people';
export const SHARE_GRAPH_SEARCH_PLACEHOLDER = 'Add people by name or email';
export const SHARE_GRAPH_NO_USERS_FOUND = 'No users found';
export const SHARE_GRAPH_LOADING_USERS = 'Loading users...';
export const SHARE_GRAPH_ACCESS_LEVEL_LABEL = 'Access Level';
export const SHARE_GRAPH_EMAIL_LABEL = 'Email Addresses';
export const SHARE_GRAPH_EMAIL_PLACEHOLDER =
  'Enter email addresses separated by comma, semicolon, or new line\nexample@email.com, another@email.com';
export const SHARE_GRAPH_HELPER_TEXT =
  'Enter one or more email addresses. Only users who already exist in the system can be granted access.';
export const SHARE_GRAPH_ACCESS_TYPE_LABEL = 'Access Type';
export const SHARE_GRAPH_ACCESS_VIEW = 'Can view';
export const SHARE_GRAPH_ACCESS_EDIT = 'Can edit';
export const SHARE_GRAPH_ACCESS_VIEW_DESCRIPTION =
  'View access: Users can view and test the graph, but cannot modify or save changes.';
export const SHARE_GRAPH_ACCESS_EDIT_DESCRIPTION =
  'Edit access: Users can view, modify, and save changes to the graph.';
export const SHARE_GRAPH_BUTTON_CANCEL = 'Cancel';
export const SHARE_GRAPH_BUTTON_SEND = 'Send';
export const SHARE_GRAPH_BUTTON_SHARE = 'Share';
export const SHARE_GRAPH_BUTTON_SHARING = 'Sharing...';
export const SHARE_GRAPH_BUTTON_DONE = 'Done';
export const SHARE_GRAPH_SUCCESS_SHARED_WITH = 'Successfully shared with';
export const SHARE_GRAPH_NOT_FOUND_IN_SYSTEM = 'Not found in system';
export const SHARE_GRAPH_ERROR_NO_EMAIL = 'Please enter at least one email address.';
export const SHARE_GRAPH_ERROR_INVALID_FORMAT = 'Invalid email format detected.';
export const SHARE_GRAPH_ERROR_NONE_FOUND = 'None of the provided emails exist in the system.';
export const SHARE_GRAPH_SUCCESS_MESSAGE = 'Graph shared successfully!';

export const SHARE_GRAPH_ERROR_PREFIX = 'Failed to share graph';
export const SHARE_GRAPH_ERROR_GENERIC = 'An error occurred while sharing the graph.';
export const SHARE_GRAPH_PEOPLE_WITH_ACCESS = 'People with access';
export const SHARE_GRAPH_REMOVE_ACCESS = 'Remove access';

export const SHARED_ACCESS_TYPE = {
  VIEW: 'view',
  EDIT: 'edit'
};

export const SHARED_BADGE_LABEL = {
  VIEW: 'SHARED (VIEW-ONLY)',
  EDIT: 'SHARED (CAN EDIT)'
};

// Graph Access Management Text Constants
export const GRAPH_ACCESS_TITLE = 'Manage Access';
export const GRAPH_ACCESS_LOADING = 'Loading access list...';
export const GRAPH_ACCESS_ERROR_GENERIC = 'Failed to load access list.';
export const GRAPH_ACCESS_NO_USERS = 'No users have access to this graph.';
export const GRAPH_ACCESS_UPDATE_SUCCESS = 'Access updated successfully!';
export const GRAPH_ACCESS_UPDATE_SUCCESS_NOTIFICATION = 'Access Updated';
export const GRAPH_ACCESS_UPDATE_ERROR = 'Failed to update access.';
export const GRAPH_ACCESS_UPDATE_ERROR_NOTIFICATION = 'Failed to update access';
export const GRAPH_ACCESS_REVOKE_SUCCESS = 'Access revoked successfully!';
export const GRAPH_ACCESS_REVOKE_ERROR = 'Failed to revoke access.';
export const GRAPH_ACCESS_REVOKE_CONFIRM = 'Are you sure you want to revoke access for this user?';
export const GRAPH_ACCESS_CHANGE_TYPE = 'Change Access Type';
export const GRAPH_ACCESS_REVOKE = 'Revoke Access';
export const GRAPH_ACCESS_OWNER = 'Owner';
export const GRAPH_ACCESS_SHARED_BY = 'Shared by';
export const GRAPH_ACCESS_SHARED_AT = 'Shared on';

// UI Icon Constants
export const ICON_CLOSE = '✕';

// History Change Types
export const CHANGE_TYPE = {
  ADD: 'ADD',
  UPDATE: 'UPDATE',
  DELETE: 'DELETE'
};

// MUI Chip Colors
export const CHIP_COLOR = {
  SUCCESS: 'success',
  WARNING: 'warning',
  ERROR: 'error',
  DEFAULT: 'default'
};

// Accessibility (ARIA) Label Constants
export const ARIA_LABEL_CLOSE = 'Close';
export const ARIA_LABEL_REMOVE_USER = 'Remove user';

/**
 * Topic count configuration for different TOC levels
 * Used by AET Topic Generation API to determine min/max topic counts
 *
 * @type {Object.<string, {minTopicCount: number, maxTopicCount: number}>}
 * @property {Object} section - Configuration for section level (mapped from 'module' type)
 * @property {number} section.minTopicCount - Minimum topics to generate (2)
 * @property {number} section.maxTopicCount - Maximum topics to generate (6)
 * @property {Object} sub-section - Configuration for sub-section level (mapped from 'slate' type)
 * @property {number} sub-section.minTopicCount - Minimum topics to generate (0)
 * @property {number} sub-section.maxTopicCount - Maximum topics to generate (2)
 *
 * @example
 * // To add a new level:
 * // 'chapter': { minTopicCount: 3, maxTopicCount: 8 }
 */
export const TOPIC_COUNT_CONFIG = {
  section: {
    minTopicCount: 0,
    maxTopicCount: 5
  },
  'sub-section': {
    minTopicCount: 0,
    maxTopicCount: 2
  }
};

// Product Model List
export const PRODUCT_MODEL_LIST = [
  { id: '1', title: 'ETEXT2_PXE' },
  { id: '2', title: 'RPLUS_EPUB' },
  { id: '3', title: 'ETEXT2_CITE' },
  { id: '4', title: 'ETEXT_EPUB_BRONTE' },
  { id: '5', title: 'ETEXT_BV_BRONTE' },
  { id: '6', title: 'RPLUS_PDF' },
  { id: '7', title: 'ET1_PDF' },
  { id: '8', title: 'ETEXT_PDF' },
  { id: '9', title: 'ETEXT1_PDF' },
  { id: '10', title: 'EXTERNAL_PVS' },
  { id: '11', title: 'EXTERNAL_REVEL' },
  { id: '12', title: 'EXTERNAL_ACTIVEHUB' }
];

export const SHOW_STATS = 'Show Stats';
export const HIDE_STATS = 'Hide Stats';
// Tool type names (from agentic tools API)
export const TOOL_TYPE = {
  REST_API: 'RestAPI',
  MCP_SERVER: 'MCPServer'
};

/** Tool types that show the Import cURL action in the implementation tab */
export const TOOLS_WITH_IMPORT_CURL = [TOOL_TYPE.REST_API];

/** Catalog `toolId` for the prebuilt run-graph RestAPI tool (agent tool picker). */
export const PREBUILT_RUN_GRAPH_TOOL_ID = 'graph';

/**
 * Whether the agentic tool entry is the prebuilt run-graph tool.
 * Prefer `toolId`; fall back to legacy display label when the API omits or varies id.
 */
export const isPrebuiltRunGraphTool = (tool) => {
  if (!tool) return false;
  if (tool.toolId === PREBUILT_RUN_GRAPH_TOOL_ID) return true;
  return tool.label === 'Graph';
};

/** HTTP methods for RestAPI `apiConfig.method` (Edit Node Implementation tab and saved graph payloads). */
export const HTTP_METHOD = {
  NONE: 'None',
  GET: 'GET',
  POST: 'POST',
  PUT: 'PUT'
};

/** RestAPI methods that send a request body (payload editor + apiConfig.payload). */
export const methodSupportsRequestBody = (method) => method === HTTP_METHOD.POST || method === HTTP_METHOD.PUT;

/** RestAPI payload editor hint — nested JSON placeholders or whole-body {{param}}. */
export const REST_API_PAYLOAD_EDITOR_PLACEHOLDER =
  '{"key": "value", "placeholder": "{{dynamicValue}}"} or {{dynamicValue}}';

// Content Tool Node — default configuration applied when adding from the Content Drawer
export const CONTENT_TOOL_CONFIG = {
  DESCRIPTION:
    'This tool provides details of the content including product name, description, TOC and other attributes of the content',
  JS_METHOD_CODE: 'async (params) => {\n\n  const data = await callApi(apiConfig, apiParams);\n\n  return data\n}',
  API_SEARCH_PATH: '/upload/search',
  API_SELECT_FIELDS: 'id,productTitle,productData',
  VECTOR_FIELD: 'productDataVector',
  QUERY_TEMPLATE: '{{query}}',
  TIMEOUT: 6000,
  RETRY: 1,
  VECTOR_K: 5,
  TOP_RESULTS: 2
};

// Edit Node Modal tab values
export const EDIT_NODE_TAB = {
  BASIC: 'basic',
  MODEL: 'model',
  MEMORY: 'memory',
  MIDDLEWARE: 'middleware',
  ADVANCED: 'advanced',
  IMPLEMENTATION: 'implementation'
};

export const EDIT_NODE_AGENT_FIELD = {
  DISPLAY_NAME: 'prompt',
  SECONDARY_NAME: 'intentType'
};
// Flow / Canvas configuration
export const FLOW_CONFIG = {
  // Node layout
  NODE_WIDTH: 220,
  NODE_HEIGHT: 120,
  NODE_GAP: 30,

  // Viewport zoom limits (used on the ReactFlow component)
  MIN_ZOOM: 0.1,
  MAX_ZOOM: 3,

  // fitView options (applied on initial render)
  FIT_VIEW_PADDING: 0.3,
  FIT_VIEW_MAX_ZOOM: 0.5
};

// Graph  Auth method
export const AUTH_METHOD = {
  BEARER: 'bearer',
  BASIC: 'basic'
};
// Graph  Auth type
export const AUTH_TYPE = {
  NONE: 'None',
  IES: 'IES',
  API_KEY: 'API Key'
};

// Auth-related header names / values (for cURL parsing and header detection)
export const AUTH_HEADERS = {
  AUTHORIZATION: 'authorization',
  X_AUTHORIZATION: 'x-authorization',
  API_KEY: 'api-key',
  BEARER_PREFIX: 'bearer '
};
// MCP Server Config constants
export const MCP_SERVER_CONFIG = {
  ID: 'MCPServerConfig',
  NAME: 'MCP Server',
  HEADING: 'MCP Server'
};
export const CONFIGURE_MCP_SERVER = 'Configure MCP Server';
export const MCP_SERVER_CONFIG_TOOL = {
  label: 'MCPServerConfig',
  description: 'This tool is to configure the MCP Server',
  toolId: 'mcp-server-config',
  id: 'MCPServer'
};
export const TEST_STATUS = Object.freeze({
  IDLE: 'idle',
  PENDING: 'pending',
  SUCCESS: 'success',
  FAILURE: 'failure'
});

/** MCP Test Connection button / OAuth lifecycle states. */
export const MCP_TEST_CONNECTION_STATE = Object.freeze({
  IDLE: 'idle',
  TESTING: 'testing',
  AUTHENTICATION_REQUIRED: 'authentication-required',
  RETEST_READY: 'retest-ready',
  RETESTING: 'retesting',
  SUCCESS: 'success',
  ERROR: 'error'
});

export const MCP_OAUTH_EVENT_TYPE = Object.freeze({
  REDIRECT: 'mcp_oauth_redirect',
  TOOLS: 'mcp_oauth_tools',
  END: 'mcp_oauth_end'
});

/** Alias for MCP OAuth redirect SSE control events. */
export const MCP_OAUTH_REDIRECT_EVENT = MCP_OAUTH_EVENT_TYPE.REDIRECT;

/** Maximum automatic OAuth resume attempts per Run Graph execution. */
export const MCP_OAUTH_MAX_RESUME_ATTEMPTS = 3;

export const MCP_OAUTH_POPUP_WINDOW_NAME = 'mcpOAuthAuth';

export const MCP_OAUTH_POPUP_FEATURES = 'width=600,height=700,scrollbars=yes,resizable=yes';

export const MCP_OAUTH_POPUP_POLL_INTERVAL_MS = 500;

export const MCP_OAUTH_ERROR_CODE = Object.freeze({
  POPUP_BLOCKED: 'MCP_OAUTH_POPUP_BLOCKED',
  NO_TOOLS: 'MCP_OAUTH_NO_TOOLS',
  INVALID_REDIRECT: 'MCP_OAUTH_INVALID_REDIRECT'
});

export const MCP_OAUTH_STREAM_MODE = Object.freeze({
  TOOLS: 'tools',
  VALIDATE: 'validate'
});

/** Response parsing mode for non-streaming MCP OAuth HTTP endpoints. */
export const MCP_OAUTH_RESPONSE_MODE = MCP_OAUTH_STREAM_MODE;

export const MCP_OAUTH_HTTP_STATUS = Object.freeze({
  UNAUTHORIZED: 401
});

export const MIDDLEWARE_LANG_KEYS = Object.freeze({
  CONFIG_ERROR: 'editNodeModal.MIDDLEWARE_CONFIG_ERROR',
  MISSING_TENANT: 'editNodeModal.MIDDLEWARE_CONFIG_MISSING_TENANT'
});

/** i18n keys for MCP config service notifications and OAuth UX. */
export const MCP_LANG_KEYS = Object.freeze({
  TEST_SUCCESS: 'editNodeModal.MCP_TEST_CONNECTION_SUCCESS',
  TEST_FAILURE: 'editNodeModal.MCP_TEST_CONNECTION_FAILURE',
  VALIDATE_FAILURE: 'editNodeModal.MCP_VALIDATE_FAILURE',
  OAUTH_POPUP_BLOCKED: 'editNodeModal.MCP_OAUTH_POPUP_BLOCKED',
  OAUTH_NO_TOOLS: 'editNodeModal.MCP_OAUTH_NO_TOOLS',
  OAUTH_INVALID_REDIRECT: 'editNodeModal.MCP_OAUTH_INVALID_REDIRECT',
  CONFIG_CREATE_SUCCESS: 'editNodeModal.MCP_CONFIG_CREATE_SUCCESS',
  CONFIG_CREATE_FAILURE: 'editNodeModal.MCP_CONFIG_CREATE_FAILURE',
  CONFIG_FETCH_FAILURE: 'editNodeModal.MCP_CONFIG_FETCH_FAILURE',
  OAUTH_COMPLETE_IN_POPUP: 'editNodeModal.MCP_OAUTH_COMPLETE_IN_POPUP',
  BUTTON_LABEL_TEST_CONNECTION: 'editNodeModal.BUTTON_LABEL_TEST_CONNECTION',
  BUTTON_LABEL_TESTING_CONNECTION: 'editNodeModal.BUTTON_LABEL_TESTING_CONNECTION',
  BUTTON_LABEL_TEST_SUCCESSFUL: 'editNodeModal.BUTTON_LABEL_TEST_SUCCESSFUL',
  BUTTON_LABEL_RESUME_TEST_CONNECTION: 'editNodeModal.BUTTON_LABEL_RESUME_TEST_CONNECTION',
  BUTTON_LABEL_AUTHENTICATING: 'editNodeModal.BUTTON_LABEL_AUTHENTICATING'
});

/** Maps backend MCP API message codes to i18n notification keys. */
export const MCP_API_MESSAGE_LANG_KEYS = Object.freeze({
  MCP_TEST_CONNECTION_SUCCESS: MCP_LANG_KEYS.TEST_SUCCESS,
  MCP_VALIDATE_SUCCESS: MCP_LANG_KEYS.TEST_SUCCESS,
  MCP_VALIDATE_FAILURE: MCP_LANG_KEYS.VALIDATE_FAILURE
});

export const SHOW_NOTIFICATION_MESSAGE = {
  SUCCESS: 'success',
  ERROR: 'error'
};

export const RUN_GRAPH_ENDPOINT_REGEX = /\/run-graph\/([^/]+)\/versions\/(\d+)/;

// Content Upload notification messages
export const UPLOAD_CONTENT_SUCCESS_MSG = 'File uploaded successfully';
export const UPLOAD_CONTENT_FAILED_MSG = 'Failed to upload file. Please try again.';
export const UPLOAD_CONTENT_TIMEOUT_MSG = 'Upload timeout. Please try again with a smaller file.';
export const UPLOAD_CONTENT_ERROR_MSG = 'Upload failed';

// Get Uploaded Content notification messages
export const GET_CONTENT_FAILED_MSG = 'Failed to fetch content';
export const GET_CONTENT_REQUEST_FAILED_MSG = 'Failed to fetch uploaded content. Please try again.';

// Delete Content notification messages
export const DELETE_CONTENT_SUCCESS_MSG = 'Content removed successfully';
export const DELETE_CONTENT_FAILED_MSG = 'Failed to remove content';
export const DELETE_CONTENT_REQUEST_FAILED_MSG = 'Failed to remove content. Please try again.';

export const PROMPT_TYPES = Object.freeze({
  SYSTEM: 'systemPrompt',
  USER: 'userPrompt'
});

/** i18n keys for Studio Prompt Management (hydration / versions read path). */
export const PROMPT_MANAGEMENT_LANG_KEYS = Object.freeze({
  ERROR_CATALOGUE: 'promptManagement.ERROR_CATALOGUE',
  ERROR_VERSIONS: 'promptManagement.ERROR_VERSIONS',
  ERROR_VERSION: 'promptManagement.ERROR_VERSION',
  ERROR_SAVE: 'promptManagement.ERROR_SAVE',
  ERROR_CREATE: 'agenticPromptMgmt.ERROR_CREATE',
  REQUEST_TIMEOUT: 'promptManagement.REQUEST_TIMEOUT',
  ERROR_NAME_AVAILABILITY: 'agenticPromptMgmt.ERROR_NAME_AVAILABILITY'
});
